import { useEffect, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { hashPin, isValidPin, verifyPin, type PinRecord } from "@ect/core";
import { BrandMark, Button } from "@ect/ui";
import { useSession } from "../app/session";

const LOCK_AFTER_MS = 5 * 60_000;
const key = (userId: string) => `ect.pin.${userId}`;

/** PIN lock (PRD M01-01): created after first login, required on launch and after 5 min in background. */
export function PinGate({ children }: { children: ReactNode }) {
  const { t } = useTranslation();
  const { session, api } = useSession();
  const userId = session!.user.id;
  const [rec, setRec] = useState<PinRecord | null>(() => { try { const r = localStorage.getItem(key(userId)); return r ? (JSON.parse(r) as PinRecord) : null; } catch { return null; } });
  const [locked, setLocked] = useState(true);
  const [pin, setPin] = useState("");
  const [confirm, setConfirm] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    let hiddenAt: number | null = null;
    const onVis = () => { if (document.visibilityState === "hidden") hiddenAt = Date.now(); else if (hiddenAt && Date.now() - hiddenAt > LOCK_AFTER_MS) setLocked(true); };
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, []);
  if (!locked) return <>{children}</>;
  const submit = async () => {
    setError(null);
    if (!rec) {
      if (confirm === null) { if (!isValidPin(pin)) return setError(t("pin.weak")); setConfirm(pin); setPin(""); return; }
      if (pin !== confirm) { setError(t("pin.mismatch")); setConfirm(null); setPin(""); return; }
      const r = await hashPin(pin);
      try { localStorage.setItem(key(userId), JSON.stringify(r)); } catch { /* private mode */ }
      setRec(r); setLocked(false); setPin(""); return;
    }
    if (await verifyPin(pin, rec)) { setLocked(false); setPin(""); } else { setError(t("pin.wrong")); setPin(""); }
  };
  const title = !rec ? (confirm === null ? t("pin.create") : t("pin.confirm")) : t("pin.unlock");
  return (
    <div className="grid min-h-dvh place-items-center bg-ground p-6">
      <div className="w-full max-w-xs text-center">
        <BrandMark className="mx-auto mb-4" /><div className="font-display text-2xl font-extrabold text-navy">{t("appName")}</div>
        <p className="mt-6 text-sm text-ink-2">{title}</p>
        <div className="my-4 flex justify-center gap-2" aria-hidden>{Array.from({ length: 6 }).map((_, i) => <span key={i} className={`h-3 w-3 rounded-full ${i < pin.length ? "bg-navy" : "border border-line"}`} />)}</div>
        <input id="pin" type="password" inputMode="numeric" autoFocus autoComplete="off" maxLength={6} value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g, ""))} onKeyDown={(e) => e.key === "Enter" && void submit()} className="sr-only" aria-label={title} />
        <div className="grid grid-cols-3 gap-2">
          {["1", "2", "3", "4", "5", "6", "7", "8", "9", "", "0", "⌫"].map((k, i) => (
            <button key={i} type="button" disabled={k === ""} onClick={() => (k === "⌫" ? setPin((p) => p.slice(0, -1)) : setPin((p) => (p.length < 6 ? p + k : p)))} className="h-14 rounded-md border border-line bg-surface font-display text-xl font-bold text-ink disabled:invisible">{k}</button>
          ))}
        </div>
        {error && <p role="alert" className="mt-3 text-sm text-danger">{error}</p>}
        <Button size="lg" className="mt-4 w-full" disabled={pin.length < 4} onClick={() => void submit()}>OK</Button>
        <button type="button" className="mt-4 text-xs text-ink-3 underline" onClick={() => void api.logout()}>{t("pin.forgot")}</button>
      </div>
    </div>
  );
}
